/**
 * VERSO THEME - Main JavaScript
 * Custom cursor, 3D carousel, scroll effects, mega menu
 */

(function() {
  'use strict';

  // ═══════════════════════════════════════════════════════════════
  // CUSTOM CURSOR
  // ═══════════════════════════════════════════════════════════════
  
  const cursor = document.querySelector('.cursor');
  const cursorDot = document.querySelector('.cursor-dot');
  
  if (cursor && cursorDot) {
    let mouseX = 0, mouseY = 0, cursorX = 0, cursorY = 0;

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      document.documentElement.style.setProperty('--cursor-x', mouseX + 'px');
      document.documentElement.style.setProperty('--cursor-y', mouseY + 'px');
      cursorDot.style.left = mouseX + 'px';
      cursorDot.style.top = mouseY + 'px';
    });

    function animateCursor() {
      cursorX += (mouseX - cursorX) * 0.12;
      cursorY += (mouseY - cursorY) * 0.12;
      cursor.style.left = cursorX + 'px';
      cursor.style.top = cursorY + 'px';
      requestAnimationFrame(animateCursor);
    }
    animateCursor();

    // Hover state for links and interactive elements
    const hoverLinks = document.querySelectorAll('a, button, .flip-card, .artwork-card, .product-card, .artist-card, .nav-frame');
    hoverLinks.forEach(el => {
      el.addEventListener('mouseenter', () => cursor.classList.add('hover-link'));
      el.addEventListener('mouseleave', () => cursor.classList.remove('hover-link'));
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // HEADER SCROLL EFFECT
  // ═══════════════════════════════════════════════════════════════
  
  const header = document.getElementById('header');
  if (header) {
    // Track whether any scrollable content is under the header
    let contentUnderHeader = false;
    
    // Main window scroll
    window.addEventListener('scroll', () => {
      if (window.scrollY > 80) {
        header.classList.add('scrolled');
      } else if (!contentUnderHeader) {
        header.classList.remove('scrolled');
      }
    });
    
    // Product info panel scroll (internal scroll container)
    const productInfoScroll = document.querySelector('.product-info-scroll');
    if (productInfoScroll) {
      productInfoScroll.addEventListener('scroll', () => {
        // If the panel has scrolled at all, content is potentially under the header
        contentUnderHeader = productInfoScroll.scrollTop > 0;
        header.classList.toggle('scrolled', contentUnderHeader || window.scrollY > 80);
      });
    }
    
    // Use Intersection Observer for any scrollable containers
    // This catches content that might scroll under the header
    const headerHeight = header.offsetHeight;
    const observerOptions = {
      root: null,
      rootMargin: `-${headerHeight}px 0px 0px 0px`,
      threshold: 0
    };
    
    const headerObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // If observed content is intersecting at the top margin, it's under the header
        if (entry.boundingClientRect.top < headerHeight && entry.isIntersecting) {
          header.classList.add('scrolled');
        }
      });
    }, observerOptions);
    
    // Observe scroll containers that might have content go under header
    document.querySelectorAll('[data-observe-header]').forEach(el => {
      headerObserver.observe(el);
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // SECTION NAVIGATION DOTS
  // ═══════════════════════════════════════════════════════════════
  
  const sections = document.querySelectorAll('section[id]');
  const navDots = document.querySelectorAll('.section-nav-dot');

  if (sections.length && navDots.length) {
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          navDots.forEach(dot => {
            dot.classList.toggle('active', dot.dataset.section === entry.target.id);
          });
        }
      });
    }, { threshold: 0.3 });

    sections.forEach(section => sectionObserver.observe(section));

    navDots.forEach(dot => {
      dot.addEventListener('click', () => {
        const target = document.getElementById(dot.dataset.section);
        if (target) target.scrollIntoView({ behavior: 'smooth' });
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // 3D CAROUSEL
  // ═══════════════════════════════════════════════════════════════
  
  const carouselTrack = document.getElementById('carouselTrack');
  const carouselItems = document.querySelectorAll('.carousel-item');
  const carouselIndicators = document.querySelectorAll('.carousel-indicator');
  const prevBtn = document.getElementById('carouselPrev');
  const nextBtn = document.getElementById('carouselNext');
  
  if (carouselTrack && carouselItems.length) {
    const itemCount = carouselItems.length;
    let currentIndex = 0;
    const angleStep = 360 / itemCount;
    const radius = 450;

    function updateCarousel() {
      carouselItems.forEach((item, i) => {
        const angle = (i - currentIndex) * angleStep;
        const radian = (angle * Math.PI) / 180;
        
        const x = Math.sin(radian) * radius;
        const z = Math.cos(radian) * radius - radius;
        const rotateY = -angle;
        
        const scale = 0.6 + (Math.cos(radian) + 1) * 0.2;
        const opacity = 0.4 + (Math.cos(radian) + 1) * 0.3;
        
        item.style.transform = `translateX(${x}px) translateZ(${z}px) rotateY(${rotateY}deg) scale(${scale})`;
        item.style.opacity = opacity;
        item.style.zIndex = Math.round((Math.cos(radian) + 1) * 50);
        
        item.classList.toggle('active', i === currentIndex);
      });
      
      carouselIndicators.forEach((ind, i) => {
        ind.classList.toggle('active', i === currentIndex);
      });
    }

    function goToSlide(index) {
      currentIndex = ((index % itemCount) + itemCount) % itemCount;
      updateCarousel();
    }

    if (nextBtn) nextBtn.addEventListener('click', () => goToSlide(currentIndex + 1));
    if (prevBtn) prevBtn.addEventListener('click', () => goToSlide(currentIndex - 1));

    carouselIndicators.forEach((ind, i) => {
      ind.addEventListener('click', () => goToSlide(i));
    });

    carouselItems.forEach((item, i) => {
      item.addEventListener('click', () => {
        if (i !== currentIndex) goToSlide(i);
      });
    });

    // Drag support
    const carouselContainer = document.getElementById('carouselContainer');
    if (carouselContainer) {
      let isDragging = false, startX = 0, dragDistance = 0;

      carouselContainer.addEventListener('mousedown', (e) => {
        isDragging = true;
        startX = e.clientX;
        carouselContainer.style.cursor = 'grabbing';
      });

      document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        dragDistance = e.clientX - startX;
      });

      document.addEventListener('mouseup', () => {
        if (!isDragging) return;
        isDragging = false;
        carouselContainer.style.cursor = 'grab';
        
        if (Math.abs(dragDistance) > 50) {
          if (dragDistance > 0) {
            goToSlide(currentIndex - 1);
          } else {
            goToSlide(currentIndex + 1);
          }
        }
        dragDistance = 0;
      });
    }

    updateCarousel();
  }

  // ═══════════════════════════════════════════════════════════════
  // FLOATING GALLERY PANEL NAVIGATION
  // ═══════════════════════════════════════════════════════════════
  
  const galleryNav = document.getElementById('galleryNav');
  const galleryNavClose = document.getElementById('galleryNavClose');
  const collectionTrigger = document.getElementById('collectionTrigger');
  const splitOverlay = document.querySelector('.split-overlay');

  function openMenu() {
    if (galleryNav) galleryNav.classList.add('active');
    document.body.classList.add('menu-open');
    document.body.style.overflow = 'hidden';
  }

  function closeMenu() {
    if (galleryNav) galleryNav.classList.remove('active');
    document.body.classList.remove('menu-open');
    document.body.style.overflow = '';
  }

  if (collectionTrigger) {
    collectionTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (galleryNav && galleryNav.classList.contains('active')) {
        closeMenu();
      } else {
        openMenu();
      }
    });
  }

  if (galleryNavClose) {
    galleryNavClose.addEventListener('click', closeMenu);
  }

  if (splitOverlay) {
    splitOverlay.addEventListener('click', closeMenu);
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && galleryNav && galleryNav.classList.contains('active')) {
      closeMenu();
    }
  });

  // ═══════════════════════════════════════════════════════════════
  // SCROLL REVEAL ANIMATIONS
  // ═══════════════════════════════════════════════════════════════
  
  const revealElements = document.querySelectorAll('.reveal-section, .type-bleed');
  
  if (revealElements.length) {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, { threshold: 0.15 });

    revealElements.forEach(el => revealObserver.observe(el));
  }

  // ═══════════════════════════════════════════════════════════════
  // STAGGERED GRID REVEAL
  // ═══════════════════════════════════════════════════════════════
  
  const staggerItems = document.querySelectorAll('.stagger-item, .artist-card, .flip-card');
  
  if (staggerItems.length) {
    const staggerObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          setTimeout(() => entry.target.classList.add('visible'), i * 100);
        }
      });
    }, { threshold: 0.1 });
    
    staggerItems.forEach(item => staggerObserver.observe(item));
  }

  // ═══════════════════════════════════════════════════════════════
  // 3D HERO PARALLAX
  // ═══════════════════════════════════════════════════════════════
  
  const hero3dScene = document.getElementById('hero3dScene');
  const heroSection = document.getElementById('hero');
  
  if (hero3dScene && heroSection) {
    document.addEventListener('mousemove', (e) => {
      const rect = heroSection.getBoundingClientRect();
      if (rect.bottom < 0) return;
      
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const rotateY = ((e.clientX - centerX) / centerX) * 8;
      const rotateX = ((e.clientY - centerY) / centerY) * -5;
      
      hero3dScene.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // HORIZONTAL SCROLL - Artists Section
  // ═══════════════════════════════════════════════════════════════
  
  const hScrollSection = document.querySelector('.h-scroll-section');
  const hScrollTrack = document.getElementById('hScrollTrack');
  const hScrollProgress = document.getElementById('hScrollProgress');
  
  if (hScrollSection && hScrollTrack) {
    // Calculate proper height based on track width
    const calculateScrollHeight = () => {
      const trackWidth = hScrollTrack.scrollWidth;
      const viewportWidth = window.innerWidth;
      const scrollDistance = Math.max(0, trackWidth - viewportWidth);
      // Height = viewport + scroll distance needed
      hScrollSection.style.height = `${window.innerHeight + scrollDistance}px`;
    };
    
    calculateScrollHeight();
    window.addEventListener('resize', calculateScrollHeight);
    
    const updateHorizontalScroll = () => {
      const rect = hScrollSection.getBoundingClientRect();
      const sectionHeight = hScrollSection.offsetHeight;
      const scrollableHeight = sectionHeight - window.innerHeight;
      
      // Calculate progress (0 to 1)
      const progress = Math.max(0, Math.min(1, -rect.top / scrollableHeight));
      
      // Calculate track translation
      const trackWidth = Math.max(0, hScrollTrack.scrollWidth - window.innerWidth);
      const translateX = progress * trackWidth;
      
      hScrollTrack.style.transform = `translateX(-${translateX}px)`;
      
      // Update progress bar
      if (hScrollProgress) {
        hScrollProgress.style.width = `${progress * 100}%`;
      }
    };
    
    window.addEventListener('scroll', updateHorizontalScroll, { passive: true });
    updateHorizontalScroll();
  }

  // ═══════════════════════════════════════════════════════════════
  // SMOOTH ANCHOR LINKS
  // ═══════════════════════════════════════════════════════════════
  
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      const href = this.getAttribute('href');
      if (href === '#' || href === '#!') return;
      e.preventDefault();
      const target = document.querySelector(href);
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    });
  });

  // ═══════════════════════════════════════════════════════════════
  // CART DRAWER
  // ═══════════════════════════════════════════════════════════════
  
  const cartDrawer = document.getElementById('cartDrawer');
  const cartDrawerClose = document.getElementById('cartDrawerClose');
  const cartDrawerBackdrop = document.getElementById('cartDrawerBackdrop');
  const cartTrigger = document.getElementById('cartTrigger');
  const headerCartCount = document.getElementById('headerCartCount');
  const cartCelebration = document.getElementById('cartCelebration');

  function openCartDrawer() {
    if (cartDrawer) {
      cartDrawer.classList.add('active');
      cartDrawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
  }
  
  // Expose globally for product page buttons
  window.openCartDrawer = openCartDrawer;

  function closeCartDrawer() {
    if (cartDrawer) {
      cartDrawer.classList.remove('active');
      cartDrawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Also close celebration if open
      if (cartCelebration) {
        cartCelebration.classList.remove('active');
      }
    }
  }

  if (cartTrigger) {
    cartTrigger.addEventListener('click', openCartDrawer);
  }

  if (cartDrawerClose) {
    cartDrawerClose.addEventListener('click', closeCartDrawer);
  }

  if (cartDrawerBackdrop) {
    cartDrawerBackdrop.addEventListener('click', closeCartDrawer);
  }

  // Celebration Animation
  function showCelebration(product) {
    if (!cartCelebration) return;
    
    const celebrationImage = document.getElementById('celebrationImage');
    const celebrationTitle = document.getElementById('celebrationTitle');
    const celebrationParticles = document.getElementById('celebrationParticles');
    
    if (celebrationImage && product.image) {
      celebrationImage.src = product.image;
    }
    if (celebrationTitle) {
      celebrationTitle.textContent = product.title;
    }
    
    // Create celebration particles
    if (celebrationParticles) {
      celebrationParticles.innerHTML = '';
      for (let i = 0; i < 20; i++) {
        const particle = document.createElement('div');
        particle.className = 'celebration-particle';
        const angle = (i / 20) * 360;
        const distance = 100 + Math.random() * 100;
        const tx = Math.cos(angle * Math.PI / 180) * distance;
        const ty = Math.sin(angle * Math.PI / 180) * distance;
        particle.style.setProperty('--tx', tx + 'px');
        particle.style.setProperty('--ty', ty + 'px');
        particle.style.left = '50%';
        particle.style.top = '40%';
        celebrationParticles.appendChild(particle);
      }
    }
    
    cartCelebration.classList.add('active');
    
    // Hide celebration after delay
    setTimeout(() => {
      cartCelebration.classList.remove('active');
    }, 2000);
  }

  // AJAX Cart Operations
  async function updateCart(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      return await response.json();
    } catch (error) {
      console.error('Cart update error:', error);
    }
  }

  async function changeCartItem(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      const cart = await response.json();
      await refreshCartDrawerSection();
      return cart;
    } catch (error) {
      console.error('Cart change error:', error);
    }
  }

  async function addToCart(variantId, quantity = 1, productData = null) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }]
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      
      const result = await response.json();
      
      // Small delay to ensure Shopify's cart is fully updated before fetching section
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Refresh drawer content using section rendering
      await refreshCartDrawerSection();
      
      // Open drawer immediately - no celebration delay, straight to checkout options
      openCartDrawer();
      
      return result;
    } catch (error) {
      console.error('Add to cart error:', error);
    }
  }
  
  // Expose globally for product page buttons
  window.addToCart = addToCart;

  // Refresh cart drawer using section rendering
  async function refreshCartDrawerSection() {
    try {
      // Add cache-busting timestamp to prevent stale responses
      const timestamp = Date.now();
      const response = await fetch(`/?sections=cart-drawer&_=${timestamp}`, {
        cache: 'no-store'
      });
      const data = await response.json();
      
      if (data['cart-drawer']) {
        // Parse the HTML - section rendering wraps content in shopify-section div
        const parser = new DOMParser();
        const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
        
        // Update the drawer content (main items area)
        const newContent = doc.querySelector('#cartDrawerContent');
        const currentContent = document.getElementById('cartDrawerContent');
        
        if (newContent && currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
          
          // Re-apply stagger animation classes after content update
          const items = currentContent.querySelectorAll('.cart-item');
          items.forEach((item, index) => {
            item.style.setProperty('--index', index);
          });
        }
        
        // Update drawer count text
        const newDrawerCount = doc.querySelector('#cartDrawerCount');
        const currentDrawerCount = document.getElementById('cartDrawerCount');
        if (newDrawerCount && currentDrawerCount) {
          currentDrawerCount.textContent = newDrawerCount.textContent;
        }
        
        // Update header cart count badge
        const cartResponse = await fetch('/cart.js', { cache: 'no-store' });
        const cart = await cartResponse.json();
        if (headerCartCount) {
          headerCartCount.textContent = cart.item_count;
          headerCartCount.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }
      }
    } catch (error) {
      console.error('Section render error:', error);
    }
  }
  
  // Expose globally for product page buttons
  window.refreshCartDrawerSection = refreshCartDrawerSection;

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Cart item quantity controls
  document.addEventListener('click', async (e) => {
    const qtyBtn = e.target.closest('.qty-btn');
    const removeBtn = e.target.closest('.cart-item-remove');
    
    if (qtyBtn) {
      const cartItem = qtyBtn.closest('.cart-item');
      const line = parseInt(cartItem.dataset.line);
      const qtyValue = cartItem.querySelector('.qty-value');
      const currentQty = parseInt(qtyValue.textContent);
      const action = qtyBtn.dataset.action;
      
      let newQty = action === 'increase' ? currentQty + 1 : currentQty - 1;
      if (newQty < 1) newQty = 0;
      
      // Optimistic UI update
      if (newQty === 0) {
        cartItem.classList.add('removing');
        setTimeout(() => {
          changeCartItem(line, 0);
        }, 400);
      } else {
        qtyValue.textContent = newQty;
        changeCartItem(line, newQty);
      }
    }
    
    if (removeBtn) {
      const cartItem = removeBtn.closest('.cart-item');
      const line = parseInt(cartItem.dataset.line);
      
      cartItem.classList.add('removing');
      setTimeout(() => {
        changeCartItem(line, 0);
      }, 400);
    }
  });

  // Expose addToCart globally for product forms
  window.VersoCart = {
    add: addToCart,
    open: openCartDrawer,
    close: closeCartDrawer
  };

  // ═══════════════════════════════════════════════════════════════
  // SEARCH MODAL - Command Palette
  // ═══════════════════════════════════════════════════════════════
  
  const searchModal = document.getElementById('searchModal');
  const searchModalBackdrop = document.getElementById('searchModalBackdrop');
  const searchModalInput = document.getElementById('searchModalInput');
  const searchTrigger = document.getElementById('searchTrigger');
  const searchResults = document.getElementById('searchResults');
  const searchResultsList = document.getElementById('searchResultsList');
  const searchDefault = document.getElementById('searchDefault');
  const searchNoResults = document.getElementById('searchNoResults');
  const searchInputWrapper = document.querySelector('.search-input-wrapper');
  const searchTabs = document.querySelectorAll('.search-tab');
  const searchSuggestions = document.querySelectorAll('.search-suggestion');

  let searchDebounceTimer;
  let currentHighlightIndex = -1;
  let currentSearchType = 'all';

  function openSearchModal() {
    if (searchModal) {
      searchModal.classList.add('active');
      searchModal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      setTimeout(() => {
        if (searchModalInput) searchModalInput.focus();
      }, 100);
    }
  }

  function closeSearchModal() {
    if (searchModal) {
      searchModal.classList.remove('active');
      searchModal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      if (searchModalInput) {
        searchModalInput.value = '';
      }
      
      // Reset to default state
      showSearchState('default');
      currentHighlightIndex = -1;
    }
  }

  function showSearchState(state) {
    if (searchDefault) searchDefault.style.display = state === 'default' ? 'block' : 'none';
    if (searchResultsList) searchResultsList.style.display = state === 'results' ? 'block' : 'none';
    if (searchNoResults) searchNoResults.style.display = state === 'empty' ? 'block' : 'none';
  }

  if (searchTrigger) {
    searchTrigger.addEventListener('click', openSearchModal);
  }

  if (searchModalBackdrop) {
    searchModalBackdrop.addEventListener('click', closeSearchModal);
  }

  // Cmd+K / Ctrl+K shortcut
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      if (searchModal && searchModal.classList.contains('active')) {
        closeSearchModal();
      } else {
        openSearchModal();
      }
    }
    
    // Escape to close modals
    if (e.key === 'Escape') {
      if (searchModal && searchModal.classList.contains('active')) {
        closeSearchModal();
      }
      if (cartDrawer && cartDrawer.classList.contains('active')) {
        closeCartDrawer();
      }
    }
    
    // Keyboard navigation for search results
    if (searchModal && searchModal.classList.contains('active')) {
      const results = searchResultsList ? searchResultsList.querySelectorAll('.search-result-item') : [];
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        currentHighlightIndex = Math.min(currentHighlightIndex + 1, results.length - 1);
        updateHighlight(results);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        currentHighlightIndex = Math.max(currentHighlightIndex - 1, 0);
        updateHighlight(results);
      } else if (e.key === 'Enter' && currentHighlightIndex >= 0) {
        e.preventDefault();
        const highlighted = results[currentHighlightIndex];
        if (highlighted) window.location.href = highlighted.href;
      }
    }
  });

  function updateHighlight(results) {
    results.forEach((result, i) => {
      result.classList.toggle('highlighted', i === currentHighlightIndex);
      if (i === currentHighlightIndex) {
        result.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  // Search input handler
  if (searchModalInput) {
    searchModalInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();
      
      clearTimeout(searchDebounceTimer);
      
      if (query.length === 0) {
        showSearchState('default');
        if (searchInputWrapper) searchInputWrapper.classList.remove('loading');
        return;
      }
      
      if (query.length < 2) return;
      
      if (searchInputWrapper) searchInputWrapper.classList.add('loading');
      
      searchDebounceTimer = setTimeout(() => {
        performSearch(query);
      }, 300);
    });
  }

  async function performSearch(query) {
    try {
      const typeParam = currentSearchType === 'all' ? '' : `&type=${currentSearchType}`;
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=10${typeParam}`);
      const data = await response.json();
      
      if (searchInputWrapper) searchInputWrapper.classList.remove('loading');
      
      renderSearchResults(data.resources.results);
    } catch (error) {
      console.error('Search error:', error);
      if (searchInputWrapper) searchInputWrapper.classList.remove('loading');
    }
  }

  function renderSearchResults(results) {
    if (!searchResultsList) return;
    
    const allResults = [
      ...(results.products || []).map(p => ({ ...p, type: 'product' })),
      ...(results.collections || []).map(c => ({ ...c, type: 'collection' })),
      ...(results.pages || []).map(p => ({ ...p, type: 'page' })),
      ...(results.articles || []).map(a => ({ ...a, type: 'article' }))
    ];
    
    // Filter by type if not "all"
    const filtered = currentSearchType === 'all' 
      ? allResults 
      : allResults.filter(r => r.type === currentSearchType);
    
    if (filtered.length === 0) {
      showSearchState('empty');
      return;
    }
    
    showSearchState('results');
    currentHighlightIndex = -1;
    
    searchResultsList.innerHTML = filtered.map(item => {
      const image = item.featured_image || item.image;
      const imageHtml = image 
        ? `<img src="${image.url || image}" alt="${item.title}">`
        : '<div class="placeholder-svg"></div>';
      
      const priceHtml = item.price 
        ? `<span class="search-result-price">$${(item.price / 100).toFixed(2)}</span>`
        : '';
      
      return `
        <a href="${item.url}" class="search-result-item">
          <div class="search-result-image">${imageHtml}</div>
          <div class="search-result-info">
            <div class="search-result-title">${item.title}</div>
            <div class="search-result-meta">
              <span class="search-result-type">${item.type}</span>
              ${item.vendor ? `<span>${item.vendor}</span>` : ''}
            </div>
          </div>
          ${priceHtml}
        </a>
      `;
    }).join('');
  }

  // Search tabs
  searchTabs.forEach(tab => {
    tab.addEventListener('click', () => {
      searchTabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      currentSearchType = tab.dataset.type;
      
      // Re-run search if there's a query
      if (searchModalInput && searchModalInput.value.trim().length >= 2) {
        performSearch(searchModalInput.value.trim());
      }
    });
  });

  // Search suggestions
  searchSuggestions.forEach(suggestion => {
    suggestion.addEventListener('click', () => {
      const query = suggestion.dataset.query;
      if (searchModalInput) {
        searchModalInput.value = query;
        performSearch(query);
      }
    });
  });

  // Expose search globally
  window.VersoSearch = {
    open: openSearchModal,
    close: closeSearchModal
  };

  // ═══════════════════════════════════════════════════════════════
  // PRODUCT PAGE - Gallery & Add to Cart
  // ═══════════════════════════════════════════════════════════════
  
  const productForm = document.getElementById('productForm');
  const galleryMainFrame = document.getElementById('galleryMainFrame');
  const galleryMainImage = document.getElementById('galleryMainImage');
  const galleryZoomLens = document.getElementById('galleryZoomLens');
  const galleryFilmstrip = document.getElementById('galleryFilmstrip');
  const filmstripThumbs = document.querySelectorAll('.filmstrip-thumb');
  const currentImageIndex = document.getElementById('currentImageIndex');
  const addToCartBtn = document.getElementById('addToCartBtn');
  const btnParticles = document.getElementById('btnParticles');
  const productVariantId = document.getElementById('productVariantId');
  const productPrice = document.getElementById('productPrice');
  
  // Product variant data
  let productVariants = [];
  const variantDataEl = document.getElementById('productVariantData');
  if (variantDataEl) {
    try {
      productVariants = JSON.parse(variantDataEl.textContent);
    } catch (e) {
      console.error('Could not parse variant data:', e);
    }
  }

  // Gallery Image Switching
  if (filmstripThumbs.length > 0 && galleryMainImage) {
    filmstripThumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        // Update active state
        filmstripThumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        
        // Update counter
        if (currentImageIndex) {
          currentImageIndex.textContent = index + 1;
        }
        
        // Crossfade image
        const newSrc = thumb.dataset.src;
        const newZoomSrc = thumb.dataset.zoomSrc;
        const newAlt = thumb.dataset.alt;
        
        galleryMainImage.style.opacity = 0;
        
        setTimeout(() => {
          galleryMainImage.src = newSrc;
          galleryMainImage.alt = newAlt;
          galleryMainImage.dataset.zoomSrc = newZoomSrc;
          galleryMainImage.style.opacity = 1;
        }, 300);
      });
    });
  }

  // Gallery Parallax Effect (subtle)
  if (galleryMainFrame) {
    const gallery = document.getElementById('productGallery');
    
    if (gallery) {
      gallery.addEventListener('mousemove', (e) => {
        const rect = gallery.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        const rotateY = ((mouseX - centerX) / centerX) * 3;
        const rotateX = ((mouseY - centerY) / centerY) * -2;
        
        galleryMainFrame.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      });
      
      gallery.addEventListener('mouseleave', () => {
        galleryMainFrame.style.transform = 'perspective(1000px) rotateX(0) rotateY(0)';
      });
    }
  }

  // Image Zoom on Hold
  if (galleryMainFrame && galleryMainImage && galleryZoomLens) {
    let isZooming = false;
    
    galleryMainFrame.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return; // Only left click
      isZooming = true;
      galleryMainFrame.classList.add('zooming');
      
      const zoomSrc = galleryMainImage.dataset.zoomSrc || galleryMainImage.src;
      galleryZoomLens.style.backgroundImage = `url(${zoomSrc})`;
      
      updateZoomPosition(e);
    });
    
    document.addEventListener('mousemove', (e) => {
      if (!isZooming) return;
      updateZoomPosition(e);
    });
    
    document.addEventListener('mouseup', () => {
      if (!isZooming) return;
      isZooming = false;
      galleryMainFrame.classList.remove('zooming');
    });
    
    function updateZoomPosition(e) {
      const rect = galleryMainFrame.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Position the lens
      galleryZoomLens.style.left = x + 'px';
      galleryZoomLens.style.top = y + 'px';
      
      // Calculate background position
      const bgX = (x / rect.width) * 100;
      const bgY = (y / rect.height) * 100;
      galleryZoomLens.style.backgroundPosition = `${bgX}% ${bgY}%`;
    }
  }

  // Variant Selection
  const optionValues = document.querySelectorAll('.option-value');
  
  if (optionValues.length > 0 && productVariants.length > 0) {
    optionValues.forEach(option => {
      option.addEventListener('click', () => {
        const optionIndex = option.closest('.product-option').dataset.optionIndex;
        const siblings = option.parentElement.querySelectorAll('.option-value');
        
        // Update selected state
        siblings.forEach(s => s.classList.remove('selected'));
        option.classList.add('selected');
        
        // Get all selected options
        const selectedOptions = [];
        document.querySelectorAll('.product-option').forEach(optionGroup => {
          const selected = optionGroup.querySelector('.option-value.selected');
          if (selected) {
            selectedOptions.push(selected.dataset.optionValue);
          }
        });
        
        // Find matching variant
        const variant = productVariants.find(v => {
          return v.options.every((opt, i) => opt === selectedOptions[i]);
        });
        
        if (variant) {
          // Update hidden input
          if (productVariantId) {
            productVariantId.value = variant.id;
          }
          
          // Update price display
          if (productPrice) {
            let priceHtml = '';
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              priceHtml += `<span class="price-compare">$${(variant.compare_at_price / 100).toFixed(2)}</span>`;
            }
            priceHtml += `<span class="price-current">$${(variant.price / 100).toFixed(2)}</span>`;
            productPrice.innerHTML = priceHtml;
          }
          
          // Update button price
          const btnPrice = document.querySelector('.product-add-btn .btn-price');
          if (btnPrice) {
            btnPrice.textContent = `$${(variant.price / 100).toFixed(2)}`;
          }
          
          // Update availability
          if (addToCartBtn) {
            addToCartBtn.disabled = !variant.available;
            const btnText = addToCartBtn.querySelector('.btn-text');
            if (btnText) {
              btnText.textContent = variant.available ? 'Add to Collection' : 'Sold Out';
            }
          }
          
          // Update URL without page reload
          const url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      });
    });
  }

  // Add to Cart with Ceremony
  if (productForm && addToCartBtn) {
    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      if (addToCartBtn.disabled || addToCartBtn.classList.contains('success')) return;
      
      const variantId = productVariantId ? productVariantId.value : null;
      if (!variantId) return;
      
      // Start animation
      addToCartBtn.classList.add('success');
      
      // Create particles
      if (btnParticles) {
        createParticles(btnParticles);
      }
      
      // Get product data for celebration
      const productImage = galleryMainImage ? galleryMainImage.src : '';
      const productTitle = document.querySelector('.product-title');
      const productData = {
        image: productImage,
        title: productTitle ? productTitle.textContent : 'Artwork'
      };
      
      // Add to cart via AJAX
      try {
        await window.VersoCart.add(parseInt(variantId), 1, productData);
      } catch (error) {
        console.error('Add to cart failed:', error);
      }
      
      // Reset button after delay
      setTimeout(() => {
        addToCartBtn.classList.remove('success');
      }, 2000);
    });
  }
  
  function createParticles(container) {
    const particleCount = 12;
    
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('span');
      particle.className = 'btn-particle';
      
      // Random direction
      const angle = (i / particleCount) * 360;
      const distance = 50 + Math.random() * 50;
      const tx = Math.cos(angle * Math.PI / 180) * distance;
      const ty = Math.sin(angle * Math.PI / 180) * distance;
      
      particle.style.setProperty('--tx', tx + 'px');
      particle.style.setProperty('--ty', ty + 'px');
      particle.style.left = '50%';
      particle.style.top = '50%';
      
      container.appendChild(particle);
      
      // Remove after animation
      setTimeout(() => particle.remove(), 800);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // QUICK BUY BUTTONS
  // ═══════════════════════════════════════════════════════════════
  
  document.addEventListener('click', async (e) => {
    const quickBuyBtn = e.target.closest('[data-quick-buy]');
    if (!quickBuyBtn) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (quickBuyBtn.classList.contains('loading') || quickBuyBtn.classList.contains('success')) {
      return;
    }
    
    const variantId = quickBuyBtn.dataset.variantId;
    const productTitle = quickBuyBtn.dataset.productTitle || 'Artwork';
    if (!variantId) return;
    
    // Get product image from parent card if available
    const productCard = quickBuyBtn.closest('.product-card, .carousel-item, .flip-card, .constellation-item');
    const productImage = productCard ? productCard.querySelector('img')?.src : '';
    
    // Start loading state
    quickBuyBtn.classList.add('loading');
    
    try {
      // Add to cart with product data for celebration
      const productData = {
        title: productTitle,
        image: productImage
      };
      
      await window.VersoCart.add(parseInt(variantId), 1, productData);
      
      // Success state
      quickBuyBtn.classList.remove('loading');
      quickBuyBtn.classList.add('success');
      
      // Reset button after animation
      setTimeout(() => {
        quickBuyBtn.classList.remove('success');
      }, 2000);
    } catch (error) {
      quickBuyBtn.classList.remove('loading');
      console.error('Quick buy error:', error);
    }
  });

  // ═══════════════════════════════════════════════════════════════
  // PAGE LOAD ANIMATION
  // ═══════════════════════════════════════════════════════════════
  
  // Stagger reveal for page elements
  document.addEventListener('DOMContentLoaded', () => {
    const revealElements = document.querySelectorAll('.reveal-on-load');
    revealElements.forEach((el, i) => {
      setTimeout(() => {
        el.classList.add('revealed');
      }, i * 100);
    });
  });

})();
